import { FunctionComponent, memo, useState } from "react";
import { useQuery } from "react-query";
import { IAnomalyCount, Route } from "@edgetier/watchtower-types";
import { SpinnerUntil } from "@edgetier/components";
import Qs from "qs";
import { useLocation } from "react-router";

import { useTimeZone } from "hooks-for/time-zones/use-time-zone";
import { Page, PageContent, PageHeader } from "@edgetier/client-components";
import AnomaliesFormFilters from "./anomalies-form-filters";

import AnomalyDateFilter from "./anomaly-date-filter";
import AnomaliesList from "./anomalies-list";
import { getInitialDateRange, getFilterData } from "./anomalies.utilities";
import { IProps } from "./anomalies.types";
import "./anomalies.scss";

/**
 * List of anomalies triggered.
 * @returns List of anomalies and a da date filter.
 */
const Anomalies: FunctionComponent<IProps> = () => {
    const location = useLocation();
    const parameters = Qs.parse(location.search, { ignoreQueryPrefix: true });

    const [initialDateRange, setInitialDateRange] = useState(() => getInitialDateRange(parameters));
    const { data: timeZone, isLoading: isTimeZoneLoading } = useTimeZone();

    // When the time zone has been requested count anomalies on each day.
    const { data: anomalyCounts, isLoading: isAnomalyCountsLoading } = useQuery(
        [Route.Anomalies, initialDateRange, timeZone],
        () => getFilterData(Route.Anomalies, initialDateRange, timeZone!),
        {
            enabled: typeof timeZone === "string",
            staleTime: Infinity,
        }
    );

    return (
        <Page className="anomalies">
            <PageHeader title="Anomalies" />
            <PageContent>
                <AnomaliesFormFilters />
                <SpinnerUntil<{ anomalyCounts: IAnomalyCount[]; timeZone: string }>
                    data={{ anomalyCounts, timeZone }}
                    isReady={!isAnomalyCountsLoading && !isTimeZoneLoading}
                >
                    {(data) =>
                        !Array.isArray(data.anomalyCounts) && typeof data.timeZone !== "string" ? (
                            "Error"
                        ) : (
                            <>
                                <AnomalyDateFilter
                                    anomalyCounts={data.anomalyCounts}
                                    initialDateRange={initialDateRange}
                                    setInitialDateRange={setInitialDateRange}
                                />
                                <AnomaliesList initialDateRange={initialDateRange} />
                            </>
                        )
                    }
                </SpinnerUntil>
            </PageContent>
        </Page>
    );
};

export default memo(Anomalies);
